import {Injectable} from '@angular/core';
import {Actions, Effect} from '@ngrx/effects';
import {Observable} from 'rxjs/Observable';
import {Action} from '@ngrx/store';
import * as auth from '../actions/authentication';
import * as routerActions from '../actions/router';
import { of } from 'rxjs/observable/of';
import { AuthenticationService } from '../services/authentication';
import { Router } from '@angular/router';
import { ErrorModel } from '../models/error-model';

@Injectable()
export class AuthenticationEffects {
  @Effect()
  login$: Observable<Action> = this.actions$
    .ofType(auth.LOGIN)
    .map((action: auth.LoginAction) => action.payload)
    .switchMap(pm => {

      const nextLookup$ = this.actions$.ofType(auth.LOGIN).skip(1);

      return this.authService.login(pm.username, pm.password, pm.totp)
        .takeUntil(nextLookup$)
        .map(u => {
          const usr = Object.assign({}, u);
          usr.returnUrl = pm.returnUrl;
          return new auth.LoginCompleteAction(usr);
        })
        .catch(e => of(new auth.ErrorAction(new ErrorModel(e, ''))));
    });

  @Effect()
  loginComplete$: Observable<Action> = this.actions$
    .ofType(auth.LOGIN_COMPLETE)
    .map((action: auth.LoginCompleteAction) => action.payload)
    .switchMap(u => {
      if (u.changePw) {
        return of(new routerActions.Go({path: ['changepassword']}));
      } else {
        return of(new routerActions.Go({ path: [u.returnUrl]}));
      }
    });

  @Effect()
  alias$: Observable<Action> = this.actions$
    .ofType(auth.ALIAS_USER)
    .map((action: auth.AliasUserAction) => action.payload)
    .switchMap(pm => {

      const nextLookup$ = this.actions$.ofType(auth.ALIAS_USER).skip(1);

      return this.authService.aliasUser(pm)
        .takeUntil(nextLookup$)
        .map(r => new auth.AliasUserCompleteAction(r))
        .catch(() => of(new auth.AliasUserCompleteAction(null)));
    });

  @Effect()
  aliasComplete$: Observable<Action> = this.actions$
    .ofType(auth.ALIAS_USER_COMPLETE)
    .filter((action: auth.AliasUserCompleteAction) => !!action.payload)
    .map(() => new routerActions.Go({ path: ['']}));

  @Effect()
  logout$: Observable<Action> = this.actions$
    .ofType(auth.LOGOUT)
    .switchMap(() => {
      const nextLookup$ = this.actions$.ofType(auth.LOGOUT).skip(1);
      return this.authService.logout()
        .takeUntil(nextLookup$)
        .map(() => new auth.LogoutCompleteAction())
        .catch(() => of(new auth.LogoutCompleteAction()));
    });

  @Effect()
  logoutComplete$: Observable<Action> = this.actions$
    .ofType(auth.LOGOUT_COMPLETE)
    .map(() => {
     sessionStorage.removeItem('access_token');
     sessionStorage.removeItem('refresh_token');
     return new routerActions.Go({ path: ['login']});
  });

  @Effect()
  resetPassword$: Observable<Action> = this.actions$
    .ofType(auth.RESET_PASSWORD)
    .map((action: auth.ResetPasswordAction) => action.payload)
    .switchMap(pm => {
      const nextLookup$ = this.actions$.ofType(auth.RESET_PASSWORD).skip(1);
      return this.authService.resetPassword(pm)
        .takeUntil(nextLookup$)
        .mergeMap(p => {
          if (p === true) {
            return [ new auth.ShowSuccessAction('Password has been reset.  A temporary password has been emailed to the user.'),
          new auth.ResetPasswordCompleteAction(p)];
        } else {
        return [new auth.ResetPasswordCompleteAction(p)];
      }
    })
        .catch(() => of(new auth.ErrorAction(new ErrorModel({},'Error resetting password.'))));
    });

  @Effect()
  changePassword$: Observable<Action> = this.actions$
    .ofType(auth.CHANGE_PASSWORD)
    .map((action: auth.ChangePasswordAction) => action.payload)
    .switchMap(pm => {

      const nextLookup$ = this.actions$.ofType(auth.CHANGE_PASSWORD).skip(1);
      return this.authService.changePassword(pm)
        .takeUntil(nextLookup$)
        .map(p => {
          if (p === true) {
            return new auth.ChangePasswordCompleteAction(p);
          } else {
            return new auth.ErrorAction(new ErrorModel({}, 'Error changing password.  Ensure current password entered is correct.'));
          }
        })
        .catch(() => of(new auth.ErrorAction(new ErrorModel({}, 'Error changing password.  Ensure current password entered is correct.'))));
    });

  @Effect()
  changePasswordComplete$: Observable<Action> = this.actions$
    .ofType(auth.CHANGE_PASSWORD_COMPLETE)
    .map(() => new routerActions.Go({ path: ['']}));

  @Effect()
  showError$: Observable<Action> = this.actions$
    .ofType(auth.SHOW_ERROR)
    .debounceTime(5000)
    .map((action: auth.ShowErrorAction) => action.payload)
      .map((m: any) => new auth.HideMessageAction());

  @Effect()
  showSuccess$: Observable<Action> = this.actions$
    .ofType(auth.SHOW_SUCCESS)
    .debounceTime(5000)
    .map(() => new auth.HideMessageAction());

  @Effect()
  error$: Observable<Action> = this.actions$
    .ofType('ERROR')
    .map((action: auth.ErrorAction) => action.payload)
      .mergeMap(m => {
        if (m.error && m.error.status == 401) {
          return [new auth.LogoutAction(),
            new auth.ShowErrorAction("Session has Expired.  Please log in.")];
        }
        return [new auth.ShowErrorAction(m.message)];
      });

  constructor(private actions$: Actions,
    private authService: AuthenticationService) { }

}



// WEBPACK FOOTER //
// C:/Users/jstan/Projects/usda-document-client/src/app/modules/core/effects/authentication.ts